/**
 * MSAL Authentication Module
 * Handles user authentication with Azure AD using MSAL
 */

import { 
  PublicClientApplication, 
  Configuration, 
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError
} from '@azure/msal-browser';
import { getConfig, AppConfig } from './config';

// Scopes required for Copilot Studio
const loginRequest = {
  scopes: ['https://api.powerplatform.com/CopilotStudio.Copilots.Invoke'],
};

// MSAL instance
let msalInstance: PublicClientApplication | null = null;

/**
 * Create MSAL configuration from runtime config
 */
function createMsalConfig(appConfig: AppConfig): Configuration {
  return {
    auth: {
      clientId: appConfig.auth.clientId,
      authority: `${appConfig.auth.authority}/${appConfig.auth.tenantId}`,
      redirectUri: appConfig.auth.redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  };
}

/**
 * Initialize MSAL instance (call after loadConfig)
 */
export async function initializeMsal(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    const appConfig = getConfig();
    const msalConfig = createMsalConfig(appConfig);
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
}

/**
 * Get the current logged-in user account
 */
export function getCurrentAccount(): AccountInfo | null {
  if (!msalInstance) return null;
  
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0];
  }
  return null;
}

/**
 * Check if user is currently logged in
 */
export function isAuthenticated(): boolean {
  return getCurrentAccount() !== null;
}

/**
 * Attempt to acquire token silently, fall back to interactive login
 * If all else fails, clears cache and forces a fresh login
 */
export async function acquireToken(): Promise<string> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  const account = getCurrentAccount();

  // Try silent token acquisition first
  if (account) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: false,
      });
      return response.accessToken;
    } catch (error) {
      console.log('Silent token acquisition failed:', error);
      
      if (error instanceof InteractionRequiredAuthError) {
        // Silent acquisition failed, need interactive login
        console.log('Interaction required, initiating popup login...');
        try {
          const response = await msalInstance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          return response.accessToken;
        } catch (popupError) {
          console.error('Popup token acquisition failed:', popupError);
          // If popup also fails, force a complete re-login
          return await forceLogin();
        }
      } else if (error instanceof BrowserAuthError) {
        // Browser auth error (e.g., interaction_in_progress, invalid tokens)
        console.log('Browser auth error, forcing re-login...');
        return await forceLogin();
      } else {
        // Unknown error - try force login as last resort
        console.error('Unknown auth error:', error);
        return await forceLogin();
      }
    }
  }

  // No account, need fresh login
  const response = await msalInstance.loginPopup(loginRequest);
  return response.accessToken;
}

/**
 * Acquire a delegated token for Microsoft Graph API.
 * Uses silent acquisition first; falls back to a popup if interaction is required.
 * Required scopes: Files.Read.All + Sites.Read.All (must be granted on the app registration)
 */
export async function acquireTokenForGraph(): Promise<string> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  const account = getCurrentAccount();
  if (!account) {
    throw new Error('No authenticated account found. User must be logged in before calling Graph.');
  }

  const graphScopes = ['Files.Read.All', 'Sites.Read.All'];

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: graphScopes,
      account,
      forceRefresh: false,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent acquisition failed (first use or consent lapsed) — show a popup
      console.log('[Graph] Silent token acquisition failed, falling back to popup...');
      const response = await msalInstance.acquireTokenPopup({
        scopes: graphScopes,
        account,
      });
      return response.accessToken;
    }
    throw error;
  }
}

/**
 * Acquire a delegated token for Azure OpenAI / Cognitive Services.
 * Uses silent acquisition first; falls back to a popup if interaction is required.
 * The user must have the "Azure AI User" RBAC role on the Azure AI Foundry project.
 * Audience is https://ai.azure.com — required for the services.ai.azure.com project endpoint.
 */
export async function acquireTokenForOpenAI(): Promise<string> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  const account = getCurrentAccount();
  if (!account) {
    throw new Error('No authenticated account found. User must be logged in before calling Azure OpenAI.');
  }

  const cognitiveScopes = ['https://ai.azure.com/.default'];

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: cognitiveScopes,
      account,
      forceRefresh: false,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.log('[OpenAI] Silent token acquisition failed, falling back to popup...');
      const response = await msalInstance.acquireTokenPopup({
        scopes: cognitiveScopes,
        account,
      });
      return response.accessToken;
    }
    throw error;
  }
}

/**
 * Force a fresh login by clearing the account cache
 * Use this when tokens are completely expired/invalid
 */
export async function forceLogin(): Promise<string> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  console.log('Forcing fresh login - clearing account cache...');

  // Clear all accounts from cache
  const accounts = msalInstance.getAllAccounts();
  for (const account of accounts) {
    msalInstance.setActiveAccount(null);
  }

  // Clear browser storage for this app
  try {
    // Get the client ID to clear the right keys
    const config = getConfig();
    const clientId = config.auth.clientId;
    
    // Remove MSAL-related items from localStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('msal') || key.includes(clientId))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Also clear sessionStorage
    const sessionKeysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('msal') || key.includes(clientId))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    console.warn('Error clearing storage:', e);
  }

  // Reinitialize MSAL
  msalInstance = null;
  await initializeMsal();

  // Do a fresh login
  const response = await msalInstance!.loginPopup(loginRequest);
  return response.accessToken;
}

/**
 * Login the user with popup
 */
export async function login(): Promise<AuthenticationResult> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  return await msalInstance.loginPopup(loginRequest);
}

/**
 * Logout the user
 */
export async function logout(): Promise<void> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }

  const account = getCurrentAccount();
  if (account) {
    await msalInstance.logoutPopup({
      account,
    });
  }
}

/**
 * Get user display name
 */
export function getUserName(): string | null {
  const account = getCurrentAccount();
  return account?.name || null;
}

/**
 * Get user email
 */
export function getUserEmail(): string | null {
  const account = getCurrentAccount();
  return account?.username || null;
}
